import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ILocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { SearchCriteriaModel } from 'stores/models';
import { SearchResultsStore } from 'stores/search-results-store';

import { LodgingPwaPropertySearch, SearchDialogMessage, SearchLinkMessage } from '__generated__/api/types';
import { LoyaltyMessagingModel } from 'models';
import { SortAndFilterModel } from 'components/common/search-form/fields/filter-types';

import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { HeaderMessageLink } from './header-message-link';
import { SearchForm } from 'components/common/search-form';
import { SWPToggle } from 'components/common/swp-toggle';

export interface ResultsSubHeaderProps {
  resultsSummary?: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
  isLoading: boolean;
  l10n: ILocalization;
  loyaltyMessagingModel?: LoyaltyMessagingModel;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
}

export const ResultsSubHeader: React.FunctionComponent<ResultsSubHeaderProps> = inject('searchResults')(
  observer(props => {
    const { resultsSummary, isLoading, l10n, loyaltyMessagingModel, searchCriteria, searchResults } = props;
    const { isError, isEmpty } = searchResults;
    const multiItem = searchCriteria && searchCriteria.misId;
    const lob = multiItem ? 'multiItem' : 'hotels';
    const searchResultsCriteria: SearchCriteriaModel = searchResults && searchResults.searchCriteria;
    const sortAndFilterOptions: SortAndFilterModel = searchResults && searchResults.sortAndFilter && searchResults.sortAndFilter.options;
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const localizedText = l10n.formatText(`${lob}.searchResults.loadingMessage.text`);

    const Loading = () => (
      <>
        {isLoading && (
          <UitkSpacing padding={{ blockend: 'two' }}>
            <UitkText inline={false} align="left" theme="default">
              {localizedText}
            </UitkText>
          </UitkSpacing>
        )}
      </>
    );

    return (
      <header>
        <Viewport>
          <ViewSmall>
            <>
              {!isEmpty && !isError && (
                <>
                  {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                  <Loading />
                  {!isLoading && <HeaderMessages resultsSummary={resultsSummary} />}
                </>
              )}
            </>
          </ViewSmall>
          <ViewLarge>
            <UitkLayoutGrid columns={8}>
              <UitkLayoutGridItem colSpan={5}>
                <div>
                  {!isEmpty && !isError && (
                    <>
                      {loyaltyMessagingModel && <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />}
                      <Loading />
                      {!isLoading && <HeaderMessages resultsSummary={resultsSummary} />}
                    </>
                  )}
                </div>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem colSpan={3}>
                <div>
                  <SearchForm
                    model={searchResultsCriteria}
                    sortAndFilterOptions={sortAndFilterOptions}
                    sortAndFilterDefaults={searchResults && searchResults.sortAndFilter && searchResults.sortAndFilter.defaults}
                    provideDefaultSubmitButton={false}
                    displayFields={['sort']}
                    ignoreFields={filters && filters.ignoreFields}
                    autoSubmit={true}
                    searchable={true}
                  />
                </div>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
          </ViewLarge>
        </Viewport>
      </header>
    );
  })
);
ResultsSubHeader.displayName = 'ResultsSubHeader';

export interface HeaderMessagesProps {
  resultsSummary?: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
}

export const HeaderMessages: React.FunctionComponent<HeaderMessagesProps> = props => {
  const { resultsSummary } = props;

  if (!resultsSummary || resultsSummary.length === 0) {
    return null;
  }

  return (
    <div data-stid="results-header-messages">
      {resultsSummary.map((message, index) => {
        const { link, value, dialogTrigger } = message;

        if (!value) {
          return null;
        }

        return (
          <UitkSpacing padding={{ blockend: 'one' }} key={`header-message-${index}`}>
            {link || dialogTrigger ? (
              <HeaderMessageLink {...message} data-stid="results-header-message" />
            ) : (
              <UitkText align="left" size={200} weight="medium" data-stid="results-header-message" key={`header-message-${index}`}>
                {value}
              </UitkText>
            )}
          </UitkSpacing>
        );
      })}
    </div>
  );
};
HeaderMessages.displayName = 'HeaderMessages';
